import styled, { css } from "styled-components";

import { ColumnAlign } from "../../../../../components/SortableColumnHeader";
import { sectionColumnHeaderCellStyles } from "../../../../../components/SortableColumnHeader/styles";
import { PriceNote } from "../../../../../components/Valuation";
import { SPACING, QUERIES } from "../../../../../utils/constants";
import {
  SectionCardRow,
  sectionInsetInline,
  sectionTableEdges,
} from "../SectionCard/styles";
import { ReturnTone } from "./types";
import Button, { BaseButton } from "../../../../../components/Button";

export {
  SectionCard as HoldingsCard,
  SectionHeaderRow as HeaderRow,
  SectionTitleBlock as TitleBlock,
  SectionTitle as Title,
  SectionSubtitle as Subtitle,
  SectionBadge as Badge,
  SectionCardList as CardList,
  SectionFooter as PagerBand,
  SectionToolbar as Toolbar,
  SectionToolbarGroup as ToolbarGroup,
  SectionSearchField as SearchField,
  SectionSearchIcon as SearchIcon,
  SectionSearchInput as SearchInput,
  SectionSearchClearButton as SearchClearButton,
  SectionNoticeInset as NoticeInset,
} from "../SectionCard/styles";

// The tint the contextual rail reads from: the one position the rail is currently describing.
const selectedSurface = css`
  background: color-mix(in srgb, var(--color-primary) 8%, transparent);
`;

const selectedHairline =
  "color-mix(in srgb, var(--color-primary) 48%, transparent)";

// A null return is neither a gain nor a loss — colouring the placeholder green
// would read as a flat position rather than a missing one.
const RETURN_TONE_COLORS: Record<ReturnTone, string> = {
  positive: "var(--color-success)",
  negative: "var(--color-error)",
  neutral: "var(--color-text-muted)",
};

export const Body = styled.div`
  min-width: 0;
`;

// Sits above the rows, not inside one: it reassures about the section as a whole after the toast fades.
export const PendingStatus = styled.p`
  margin: 0 0 ${SPACING.sm + SPACING.xs}px;
  ${sectionInsetInline("margin")}
  padding: ${SPACING.sm}px ${SPACING.md}px;
  border-radius: ${SPACING.xs}px;
  background: var(--color-info-background);
  border: 1px solid var(--color-info-border);
  color: var(--color-text-secondary);
  font-size: var(--text-sm);
`;

// The actions column carries no visible heading, but it is still a column: the
// label keeps it announced without widening the header visually.
export const HiddenHeaderLabel = styled.span`
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip-path: inset(50%);
  white-space: nowrap;
`;

// No horizontal-scroll wrapper unlike Savings/Liabilities: this only renders above
// BREAKPOINTS.tabletLarge (Holdings.tsx), where cards take over below instead.
export const Table = styled.table`
  width: 100%;
  table-layout: fixed;
  border-collapse: collapse;

  th {
    padding: ${SPACING.sm + SPACING.xxs / 2}px ${SPACING.sm + SPACING.xs}px;
    letter-spacing: 0.055em;
  }

  @media ${QUERIES.snapshotSplitAndDown} {
    th,
    td {
      padding-inline: ${SPACING.xs + SPACING.xxs}px;
      font-size: var(--text-xs);
    }

    th {
      letter-spacing: 0.035em;
    }
  }

  ${sectionTableEdges}
`;

// Shared whole with the sortable <th>s beside it in this same row; only the width is this table's own.
export const HeaderCell = styled.th<{
  $align?: ColumnAlign;
  $width: string;
}>`
  ${sectionColumnHeaderCellStyles}
  width: ${({ $width }) => $width};
`;

export const Row = styled.tr<{ $selected: boolean }>`
  border-bottom: 1px solid var(--color-card-border);
  cursor: pointer;

  ${({ $selected }) =>
    $selected
      ? css`
          ${selectedSurface}
          box-shadow:
            inset 0 1px 0 ${selectedHairline},
            inset 0 -1px 0 ${selectedHairline};
        `
      : css`
          &:hover {
            background: var(--hover-background);
          }
        `}
`;

// Keyboard and screen-reader entry point; pointer users can click anywhere on the row.
export const InspectPositionButton = styled(BaseButton)`
  display: block;
  width: 100%;
  min-width: 0;
  padding: ${SPACING.xs}px;
  border: 0;
  border-radius: 8px;
  background: transparent;
  color: inherit;
  font: inherit;
  text-align: left;
  cursor: pointer;

  /* Inset ring: the table clips an outer one. */
  &:focus-visible {
    outline: 0;
    box-shadow: inset 0 0 0 2px var(--color-primary);
  }
`;

export const CardRow = styled(SectionCardRow)<{ $selected: boolean }>`
  cursor: pointer;
  ${({ $selected }) => $selected && selectedSurface}
`;

// Keyboard and screen-reader entry point; pointer users can tap anywhere on the card.
export const CardInspectButton = styled(BaseButton)`
  min-width: 0;
  padding: ${SPACING.xxs}px ${SPACING.xs}px;
  margin: -${SPACING.xxs}px -${SPACING.xs}px;
  border: 0;
  border-radius: 8px;
  background: transparent;
  color: inherit;
  font: inherit;
  text-align: left;
  cursor: pointer;

  &:hover {
    background: var(--hover-background);
  }
`;

export const Cell = styled.td`
  padding: ${SPACING.md - SPACING.xxs - SPACING.xxs / 2}px
    ${SPACING.sm + SPACING.xs}px;
  font-size: var(--text-sm);
  font-variant-numeric: tabular-nums;
  line-height: 1.35;
  vertical-align: middle;
`;

// A `td` that is itself a flex/grid container loses its cell semantics in some browsers.
export const NameStack = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
`;

export const Instrument = styled.div`
  display: flex;
  align-items: center;
  gap: ${SPACING.sm + SPACING.xxs}px;
  min-width: 0;
`;

export const InstrumentMark = styled.span<{ $color: string }>`
  display: grid;
  flex: 0 0 auto;
  place-items: center;
  width: 30px;
  height: 30px;
  border: 1px solid
    color-mix(in srgb, ${({ $color }) => $color} 35%, transparent);
  border-radius: 8px;
  background: color-mix(in srgb, ${({ $color }) => $color} 10%, transparent);
  color: var(--color-text-default);
  font-size: var(--text-xs);
  font-weight: 700;
`;

// The table only renders from tabletLarge up, so this band is where the instrument column is narrowest.
export const InspectHint = styled.span`
  margin-top: ${SPACING.xxs}px;
  color: var(--color-primary);
  font-size: var(--text-xs);
  font-weight: 700;
  white-space: nowrap;

  @media ${QUERIES.snapshotSplitAndDown} {
    display: none;
  }
`;

export const KebabAlign = styled.div`
  display: flex;
  justify-content: flex-end;
`;

export const InstrumentSymbol = styled.span`
  color: var(--color-text-default);
  font-size: var(--text-base);
  line-height: 1.2;
  font-weight: 700;
`;

export const InstrumentName = styled.span`
  overflow: hidden;
  color: var(--color-text-secondary);
  font-size: var(--text-xs);
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const QuantityText = styled.span`
  color: var(--color-text-default);
  font-weight: 600;
`;

// Two of the table's cells need this figure-then-note stack, so it's defined once.
const StackedCell = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
`;

// The provenance line stacks under the price it dates, which is the figure it
// has always described.
export const PriceStack = StackedCell;

export const PriceAmount = styled.span`
  color: var(--color-text-default);
`;

export const CostText = styled.span`
  color: var(--color-text-secondary);
`;

// The valuation note and the private-price remedy stack under the amount.
export const ValueStack = StackedCell;

// The amount and its state badge sit on one line so the badge reads as a
// qualifier on that figure rather than as a row-level status of its own.
export const ValueRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${SPACING.xs}px;
  min-width: 0;
`;

export const ValueAmount = styled.span`
  color: var(--color-text-default);
  font-weight: 700;
`;

// Shown only where there is a quote: it dates the price above it, and dating a
// number that isn't there would be meaningless.
export const PriceUpdatedNote = styled(PriceNote)`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

// Wraps rather than ellipsising: it carries valuationDetailFor's detail copy, and a clipped
// explanation would lose exactly the half that says what to do about the missing figure.
export const ValuationNote = styled.span`
  color: var(--color-text-muted);
  font-size: var(--text-xs);
  line-height: 1.4;
  white-space: normal;
`;

export const PriceOverrideButton = styled(Button)`
  align-self: flex-start;
  margin-top: ${SPACING.xs}px;
`;

export const AllocationNote = styled.span`
  color: var(--color-text-muted);
  font-size: var(--text-xs);
`;

export const ReturnStack = StackedCell;

export const ReturnAmount = styled.span<{ $tone: ReturnTone }>`
  color: ${({ $tone }) => RETURN_TONE_COLORS[$tone]};
  font-weight: 700;
`;

export const ReturnPercent = styled.span`
  color: var(--color-text-muted);
  font-size: var(--text-xs);
`;

export const CardTopRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${SPACING.md}px;
`;

// The return and the kebab travel together on the trailing edge, so the symbol
// keeps the leading one to itself.
export const CardTopActions = styled.div`
  display: flex;
  align-items: center;
  gap: ${SPACING.xs}px;
`;

export const CardName = styled.span`
  color: var(--color-text-default);
  font-size: var(--text-lg);
  font-weight: 700;
`;

export const CardInstrument = styled.span`
  overflow: hidden;
  color: var(--color-text-secondary);
  font-size: var(--text-base);
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const CardValueRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${SPACING.sm}px;
  flex-wrap: wrap;
`;

export const CardValue = styled.span`
  color: var(--color-text-default);
  font-size: var(--text-xxl);
  font-weight: 700;
`;

export const CardValuationNote = styled.span`
  color: var(--color-text-muted);
  font-size: var(--text-base);
`;

export const CardPriceUpdatedNote = styled(PriceNote)`
  font-size: var(--text-sm);
`;

export const CardMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${SPACING.sm}px;
  color: var(--color-text-secondary);
  font-size: var(--text-base);
`;

// Keeps the price and its dating line wrapping as a unit alongside Quantity and Cost inside CardMeta.
export const CardPriceMeta = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACING.xxs}px;
`;

// A figure inside `CardMeta`, which is muted by default: the numbers that carry
// meaning step back out of it rather than reading as labels.
const CardFigure = styled.span`
  color: var(--color-text-default);
  font-weight: 600;
`;

export const CardQuantity = CardFigure;

// Same emphasis as the quantity beside it — bare, it would inherit CardMeta's muted tone.
export const CardPrice = CardFigure;

export const CardReturn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
`;
